import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SelectEntity from "components/SelectEntity";
import SnackbarMessage from "components/SnackbarMessage";
import { differenceInDays, isValid } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Email,
  EmailFilter,
  EmailResendRequest,
  ListRequestOfEmailFilter,
  ListResponseOfEmail,
  ListResponseOfEmailTemplateModel,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { getUtcDate, standardDateFormat } from "utils/dateUtils";
import CustomActionBar from "./CustomActionBar";
import EmailActions from "./EmailActions";
import EmailProviderIcon from "./EmailProviderIcon";

const EmailsList = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { formatMessage: t } = useIntl();
  const { emailService, emailDetaisUrl } = useGlobalContext();
  const searchTerm = persistanceStore.useState((s) => s.emailSearchTerm);
  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 500);

  const { organizationId } = contextStore.useState();

  const emailTemplateId = persistanceStore.useState((s) => s.emailTemplateId);
  const emailStartDateSearch = persistanceStore.useState(
    (s) => s.emailStartDateSearch
  );
  const emailEndDateSearch = persistanceStore.useState(
    (s) => s.emailEndDateSearch
  );
  const [forceRenderKey, setForceRenderKey] = useState(0);

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  useListTitle("sentEmails");

  const emailTemplatesResponse = useQuery<ListResponseOfEmailTemplateModel>(
    ["emailService.getUsedEmailTemplates", organizationId],
    () => emailService.getUsedEmailTemplates()
  );

  const isValidDateRange = (start?: Date, end?: Date) => {
    return (
      start &&
      end &&
      isValid(start) &&
      isValid(end) &&
      differenceInDays(end, start) <= 7
    );
  };

  const canApplyFilters = useCallback(() => {
    return isValidDateRange(emailStartDateSearch, emailEndDateSearch);
  }, [emailStartDateSearch, emailEndDateSearch]);

  const {
    isError,
    data,
    error,
    isFetching,
    refetch,
  } = useQuery<ListResponseOfEmail>(
    [
      "emails",
      organizationId,
      page,
      rowsPerPage,
      emailTemplateId,
      debouncedSearchTerm,
      emailStartDateSearch,
      emailEndDateSearch,
      orderBy,
      order,
    ],
    () =>
      emailService.getEmails(
        ListRequestOfEmailFilter.fromJS({
          sortField: orderBy,
          sortDescending: order === "desc",
          pageSize: rowsPerPage,
          pageNumber: page,
          parameters: EmailFilter.fromJS({
            searchText: debouncedSearchTerm,
            dateFrom: isValid(emailStartDateSearch)
              ? getUtcDate(emailStartDateSearch!)
              : undefined,
            dateTo: isValid(emailEndDateSearch)
              ? getUtcDate(emailEndDateSearch!)
              : undefined,
            templateId: emailTemplateId,
          }),
        })
      ),
    { enabled: true, keepPreviousData: true }
  );

  useEffect(() => setPage(0), [
    debouncedSearchTerm,
    emailTemplateId,
    emailStartDateSearch,
    emailEndDateSearch,
    organizationId,
  ]);
  const onUpdateDate = (date: Date | undefined, type: "start" | "end") => {
    const updatedStart = type === "start" ? date : emailStartDateSearch;
    const updatedEnd = type === "end" ? date : emailEndDateSearch;

    const isSearchOrSortRestricted =
      searchTerm.trim() !== "" || orderBy !== "createdOn";
    if (
      isSearchOrSortRestricted &&
      !isValidDateRange(updatedStart, updatedEnd)
    ) {
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "emailsValidationSearch" }),
          Severity.Warning
        )
      );
      // force re-render to override mui picker internal state
      setForceRenderKey((prev) => prev + 1);
      return;
    }

    persistanceStore.update((s) => {
      s[type === "start" ? "emailStartDateSearch" : "emailEndDateSearch"] =
        date && isValid(date) ? date : undefined;
    });
  };

  const onClearDate = (type: "start" | "end") => {
    onUpdateDate(undefined, type);
  };

  const resendEmail = async (email: Email) => {
    try {
      const request = new EmailResendRequest({
        emailId: email.id,
        organizationId: email.organizationId,
      });
      await emailService.resendEmail(request);
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "emailResendSuccess" }),
          Severity.Success
        )
      );
      refetch();
    } catch (error) {
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "emailResendFailure" }),
          Severity.Error
        )
      );
    }
  };

  const emails =
    data?.data?.map((x) => ({
      ...x,
      id: x.id!,
    })) ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "createdOn",
      align: "left",
      type: "date",
      label: t({ id: "sentLabel" }),
    },

    {
      accessor: "senderEmail",
      align: "left",
      type: "text",
      label: t({ id: "senderLabel" }),
    },

    {
      accessor: "toEmails",
      align: "left",
      type: "text",
      label: t({ id: "recipientLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      type: "text",
      label: t({ id: "organization" }),
    },
    {
      accessor: "emailTemplateName",
      customSortAccessor: "emailTemplate.name",
      align: "left",
      type: "text",
      label: t({ id: "templateLabel" }),
    },
    {
      accessor: "emailProvider",
      align: "center",
      type: "node",
      noSort: true,
      label: t({ id: "provider" }),
      View: (row: { value: Email }) => (
        <EmailProviderIcon provider={row.value.emailProvider} />
      ),
    },

    {
      accessor: "",
      align: "left",
      type: "node",
      noSort: true,
      label: t({ id: "actions" }),
      View: (row: { value: Email }) => {
        return (
          <EmailActions
            onView={() => navigate(emailDetaisUrl(row.value.id))}
            onResend={() => resendEmail(row.value)}
          />
        );
      },
    },
  ];

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <Container>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.emailSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                if (e.target.value.trim() && !canApplyFilters()) {
                  setSnackbarMessage(
                    new SnackbarMessageProps(
                      t({ id: "emailsValidationSearch" }),
                      Severity.Warning
                    )
                  );
                  return;
                }
                persistanceStore.update((s) => {
                  s.emailSearchTerm = e.target.value;
                });
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <SelectEntity
              value={emailTemplateId || ""}
              getOptionLabel={(option) =>
                option.name + ` (${option.organizationName})`
              }
              fetchedData={emailTemplatesResponse}
              labelField="name"
              label={t({ id: "templateLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.emailTemplateId = id;
                })
              }
              textFieldProps={{ variant: "outlined" }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              key={`${
                emailStartDateSearch?.toISOString() ?? "empty-start"
              }-${forceRenderKey}`}
              label={t({ id: "startDateLabel" })}
              disabled={isFetching}
              value={emailStartDateSearch || null}
              format={standardDateFormat}
              onChange={(date) => onUpdateDate(date ?? undefined, "start")}
              slots={{
                actionBar: (props) => (
                  <CustomActionBar
                    {...props}
                    onCustomClear={() => onClearDate("start")}
                  />
                ),
              }}
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: { actions: ["clear", "cancel"] },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              key={`${
                emailEndDateSearch?.toISOString() ?? "empty-end"
              }-${forceRenderKey}`}
              label={t({ id: "endDateLabel" })}
              disabled={isFetching}
              value={emailEndDateSearch || null}
              format={standardDateFormat}
              onChange={(date) => onUpdateDate(date ?? undefined, "end")}
              slots={{
                actionBar: (props) => (
                  <CustomActionBar
                    {...props}
                    onCustomClear={() => onClearDate("end")}
                  />
                ),
              }}
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: { actions: ["clear", "cancel"] },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: emails ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "sentEmails" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy: (newOrderBy) => {
            const isCreatedOn = newOrderBy === "createdOn";
            if (!isCreatedOn && !canApplyFilters()) {
              setSnackbarMessage(
                new SnackbarMessageProps(
                  t({ id: "emailsValidationSearch" }),
                  Severity.Warning
                )
              );
              return;
            }
            setOrderBy(newOrderBy);
          },
          rowsPerPage,
          setRowsPerPage,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => emailDetaisUrl(id)),
          ],
        }}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default EmailsList;
